import React, { useState, useRef, useEffect } from 'react'
import {
    Container, Typography, TextField, Button, Grid,
    MenuItem, Select, InputLabel, FormControl, CircularProgress, Box
} from '@mui/material'
import NatureIcon from '@mui/icons-material/Nature';
import YardIcon from '@mui/icons-material/Yard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Card from '../components/Card'
import { postLandscapingRequest } from '../backend/api'
import { EMAIL_REGEX } from '../resources/constants'
import setStateAsync from '../backend/setStateAsync'

const SERVICE_TYPES = [
    { value: 'lawn_care',            label: 'Lawn Care' },
    { value: 'landscaping_design',   label: 'Landscaping Design' },
    { value: 'foundation_excavation',         label: 'Foundation Excavation' },
    { value: 'garden_maintenance',   label: 'Garden Maintenance' },
    { value: 'road_construction',    label: 'Road Construction' },
    { value: 'land_clearing',         label: 'Land Clearing' },
]

const PARISHES = [
    { value: 'saint_andrew',  label: 'Saint Andrew' },
    { value: 'saint_david',   label: 'Saint David' },
    { value: 'saint_george',  label: 'Saint George' },
    { value: 'saint_john',    label: 'Saint John' },
    { value: 'saint_joseph',  label: 'Saint Joseph' },
    { value: 'saint_luke',    label: 'Saint Luke' },
    { value: 'saint_mark',    label: 'Saint Mark' },
    { value: 'saint_patrick', label: 'Saint Patrick' },
    { value: 'saint_paul',    label: 'Saint Paul' },
    { value: 'saint_peter',   label: 'Saint Peter' },
]

export default function LandscapingScreen() {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [serviceType, setServiceType] = useState('')
    const [parish, setParish] = useState('')
    const [address, setAddress] = useState('')
    const [description, setDescription] = useState('')

    const [loading, toggleLoading] = useState(false)
    const [error, toggleError] = useState(false)
    const [success, toggleSuccess] = useState(false)

    const mounted = useRef(true)
    useEffect(() => { return () => { mounted.current = false } }, [])

    function isValid() {
        return (
            name.trim().length > 0 &&
            EMAIL_REGEX.test(email) &&
            serviceType.length > 0 &&
            parish.length > 0 &&
            description.trim().length > 0
        )
    }

    async function handleSubmit() {
        try {
            setStateAsync(true, toggleLoading, mounted.current)
            setStateAsync(false, toggleError, mounted.current)
            await postLandscapingRequest({ name, email, phone, service_type: serviceType, parish, address, description })
            setStateAsync(true, toggleSuccess, mounted.current)
        } catch (e) {
            setStateAsync(true, toggleError, mounted.current)
        } finally {
            setStateAsync(false, toggleLoading, mounted.current)
        }
    }

    function reset() {
        setName(''); setEmail(''); setPhone(''); setServiceType('')
        setParish(''); setAddress(''); setDescription('')
        toggleSuccess(false); toggleError(false)
    }

    return (
        <Container maxWidth='md' sx={{ py: 5 }}>
            {/* Hero header */}
            <Box display='flex' flexDirection='column' alignItems='center' mb={4}>
                <NatureIcon sx={{ fontSize: 60, color: 'text.primaryGreen' }} />
                <Typography variant='h4' textAlign='center' mt={1}>Landscaping Services</Typography>
                <Typography variant='body1' textAlign='center' mt={1} color='text.secondary' maxWidth={550}>
                    From lawn care to full landscaping design, our team covers every parish in Dominica.
                    Fill in the form below and we will be in touch within 24 hours.
                </Typography>
            </Box>

            {/* Service highlights */}
            <Grid container spacing={2} mb={4} justifyContent='center'>
                {SERVICE_TYPES.map(s => (
                    <Grid item xs={6} sm={4} key={s.value}>
                        <Card style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 12, backgroundColor: 'background.lightSecondary' }}>
                            <YardIcon sx={{ color: 'text.primaryGreen', mb: 0.5 }} />
                            <Typography variant='body2' textAlign='center'>{s.label}</Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Request form */}
            <Card style={{ padding: 24 }}>
                {success ? (
                    <Box display='flex' flexDirection='column' alignItems='center' py={4} gap={2}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'text.primaryGreen' }} />
                        <Typography variant='h5' textAlign='center'>Request Received!</Typography>
                        <Typography variant='body2' textAlign='center' color='text.secondary'>
                            We will contact you soon to discuss your landscaping needs.
                        </Typography>
                        <Button variant='outlined' onClick={reset}>Submit another request</Button>
                    </Box>
                ) : (
                    <>
                        <Typography variant='h6' textAlign='center' mb={2}>Request a Service</Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label='Full Name' required
                                    value={name} onChange={e => setName(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label='Email Address' type='email' required
                                    value={email} onChange={e => setEmail(e.target.value)}
                                    error={email.length > 0 && !EMAIL_REGEX.test(email)}
                                    helperText={email.length > 0 && !EMAIL_REGEX.test(email) ? 'Enter a valid email' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label='Phone (optional)' type='tel'
                                    value={phone} onChange={e => setPhone(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Service Type</InputLabel>
                                    <Select
                                        value={serviceType}
                                        label='Service Type'
                                        onChange={e => setServiceType(e.target.value)}
                                    >
                                        {SERVICE_TYPES.map(s => (
                                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Parish</InputLabel>
                                    <Select
                                        value={parish}
                                        label='Parish'
                                        onChange={e => setParish(e.target.value)}
                                    >
                                        {PARISHES.map(p => (
                                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label='Property Address (optional)'
                                    value={address} onChange={e => setAddress(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth multiline rows={4} label='Description of work needed' required
                                    value={description} onChange={e => setDescription(e.target.value)}
                                    helperText='Please describe what you need done and any relevant details.'
                                />
                            </Grid>
                        </Grid>

                        {error && (
                            <Typography color='error' textAlign='center' mt={2}>
                                Something went wrong. Please try again.
                            </Typography>
                        )}

                        <Box display='flex' justifyContent='center' mt={3}>
                            {loading
                                ? <CircularProgress />
                                : <Button
                                    variant='contained'
                                    size='large'
                                    disabled={!isValid()}
                                    onClick={handleSubmit}
                                >
                                    Submit Request
                                </Button>
                            }
                        </Box>
                    </>
                )}
            </Card>
        </Container>
    )
}
